import React, { memo, useCallback, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";

interface DecorLabelProps {
  name: string;
  rotation: number;
  isDark: boolean;
}

const DecorLabelComponent: React.FC<DecorLabelProps> = ({
  name,
  rotation,
  isDark
}) => {
  const textRef = useRef<HTMLSpanElement>(null);
  const [isTruncated, setIsTruncated] = useState(false);
  const [showTooltip, setShowTooltip] = useState(false);
  const [tooltipPosition, setTooltipPosition] = useState({ top: 0, left: 0 });
  const tooltipRef = useRef<HTMLDivElement>(null);
  const touchTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const checkTruncation = useCallback(() => {
    if (!textRef.current) return;
    setIsTruncated(textRef.current.scrollWidth > textRef.current.clientWidth);
  }, []);

  useEffect(() => {
    requestAnimationFrame(checkTruncation);
    window.addEventListener("resize", checkTruncation);
    return () => window.removeEventListener("resize", checkTruncation);
  }, [checkTruncation]);

  const updateTooltipPosition = useCallback(() => {
    if (!textRef.current) return;
    const rect = textRef.current.getBoundingClientRect();
    const viewportWidth = window.innerWidth;
    const tooltipWidth = tooltipRef.current?.offsetWidth ?? 0;

    let left = rect.left + rect.width / 2 - tooltipWidth / 2;
    const padding = 8;
    if (left < padding) left = padding;
    if (left + tooltipWidth > viewportWidth - padding) {
      left = viewportWidth - tooltipWidth - padding;
    }

    setTooltipPosition({
      top: rect.top - 4,
      left
    });
  }, []);

  useEffect(() => {
    if (showTooltip) {
      requestAnimationFrame(updateTooltipPosition);
    }
  }, [showTooltip, updateTooltipPosition]);

  useEffect(() => {
    return () => {
      if (touchTimeoutRef.current) clearTimeout(touchTimeoutRef.current);
    };
  }, []);

  const handleMouseEnter = useCallback((): void => {
    if (!isTruncated) return;
    setShowTooltip(true);
  }, [isTruncated]);

  const handleMouseLeave = useCallback((): void => {
    setShowTooltip(false);
  }, []);

  const handleTouchStart = useCallback(
    (e: React.TouchEvent): void => {
      if (!isTruncated) return;
      e.stopPropagation();
      setShowTooltip(true);
      if (touchTimeoutRef.current) clearTimeout(touchTimeoutRef.current);
      touchTimeoutRef.current = setTimeout(() => {
        setShowTooltip(false);
      }, 2000);
    },
    [isTruncated]
  );

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent): void => {
      if (!isTruncated) return;
      if (e.key === "Enter" || e.key === " ") {
        e.preventDefault();
        setShowTooltip((prev) => !prev);
      }
      if (e.key === "Escape") {
        setShowTooltip(false);
      }
    },
    [isTruncated]
  );

  const sharedStyle: React.CSSProperties = {
    transform: `rotate(${-rotation}deg)`
  };

  const content = (
    <>
      <span
        ref={textRef}
        className="text-xs font-bold truncate max-w-full text-center"
        style={{ color: isDark ? "#94a3b8" : "#64748b" }}
      >
        {name}
      </span>

      {showTooltip &&
        isTruncated &&
        createPortal(
          <div
            ref={tooltipRef}
            role="tooltip"
            className="fixed z-[99999] rounded-md bg-slate-900 px-3 py-1.5 text-xs text-white shadow-md animate-in fade-in-0 zoom-in-95 dark:bg-slate-800 dark:text-slate-100 -translate-y-full pointer-events-none"
            style={{
              top: `${tooltipPosition.top}px`,
              left: `${tooltipPosition.left}px`
            }}
          >
            {name}
          </div>,
          document.body
        )}
    </>
  );

  if (isTruncated) {
    return (
      <button
        type="button"
        className="absolute inset-0 flex items-center justify-center overflow-hidden px-1 bg-transparent border-0 p-0 m-0 cursor-pointer"
        style={{ ...sharedStyle, pointerEvents: "auto" }}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
        onTouchStart={handleTouchStart}
        onFocus={handleMouseEnter}
        onBlur={handleMouseLeave}
        onKeyDown={handleKeyDown}
        aria-label={name}
      >
        {content}
      </button>
    );
  }

  return (
    <div
      className="absolute inset-0 flex items-center justify-center overflow-hidden px-1"
      style={{ ...sharedStyle, pointerEvents: "none" }}
    >
      {content}
    </div>
  );
};

export const DecorLabel = memo(DecorLabelComponent);
